/**
 * Phase F / C-4 — Rotating loading-message hook.
 *
 * `useLoadingMessage(context)` returns one string from
 * `LOADING_POOLS[context]` and rotates to the next one every
 * `LOADING_MESSAGE_ROTATION_MS` (Q34 single source).
 *
 * Rotation order: seeded shuffle on first mount so the same context
 * shows variety across renders without being predictable. Per Q33
 * pools are strict-per-context (no cross-pool overlap), so each
 * context's user perceives a tight family of strings.
 *
 * Plan: ~/.claude/plans/it-s-time-to-rustling-locket.md §"Phase F" + C-4.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  LOADING_POOLS,
  type LoadingContext,
} from './loadingMessages';
import { LOADING_MESSAGE_ROTATION_MS } from './uiTimingConfig';

/**
 * Mulberry32 — tiny seeded RNG. Deterministic per seed so tests can
 * pin the rotation order without monkey-patching Math.random.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function rand(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fisher-Yates shuffle keyed by a seeded RNG. Returns a NEW array;
 * does not mutate the source pool.
 */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const out = items.slice();
  const rand = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

/**
 * Pick the first string for a given context deterministically. The
 * same `context` returns the same first string on every render — the
 * rotation order downstream varies per mount. Used by tests + the
 * smoke harness to assert "first string is stable per context".
 */
export function firstMessageForContext(context: LoadingContext): string {
  const pool = LOADING_POOLS[context];
  // Seed off a tiny hash of the context name so each context picks
  // a different first slot from the unshuffled pool.
  let seed = 0;
  for (let i = 0; i < context.length; i++) {
    seed = (seed * 31 + context.charCodeAt(i)) | 0;
  }
  const order = seededShuffle(pool, seed);
  return order[0] ?? pool[0]!;
}

interface UseLoadingMessageOptions {
  /**
   * Override the rotation interval. Phase F default is the shared
   * `LOADING_MESSAGE_ROTATION_MS` from `uiTimingConfig.ts`; tests pass
   * a smaller number so they don't have to fake timers all over.
   */
  rotationMs?: number;
  /**
   * Optional seed. When omitted, the hook seeds off a Math.random
   * draw at mount time so each mount shuffles independently. When
   * provided (typically by tests), rotation is fully deterministic.
   */
  seed?: number;
}

/**
 * Rotates through `LOADING_POOLS[context]` every `rotationMs`
 * milliseconds. Stable string until the rotation interval elapses;
 * after that the next-shuffled-string is returned.
 */
export function useLoadingMessage(
  context: LoadingContext,
  options: UseLoadingMessageOptions = {},
): string {
  const rotationMs = options.rotationMs ?? LOADING_MESSAGE_ROTATION_MS;
  // Pick a seed once per mount. When the caller passes a seed, use it
  // for full determinism; otherwise derive one from Math.random so
  // two mounts of the same context yield different orders.
  const seed = useMemo<number>(() => {
    if (options.seed !== undefined) return options.seed;
    // Disabling the comment to keep the seed scope local; Math.random
    // is acceptable here because the rotation is purely cosmetic.
    return Math.floor(Math.random() * 2 ** 31);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const order = useMemo<readonly string[]>(
    () => seededShuffle(LOADING_POOLS[context], seed),
    [context, seed],
  );

  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    if (order.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % order.length);
    }, rotationMs);
    return () => {
      window.clearInterval(id);
    };
  }, [order, rotationMs]);

  return order[index] ?? order[0] ?? '';
}
